import TeamsOrganizer from "../components/teams/TeamsOrganizer";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ApiService } from "services/ApiService";
import { Team } from "models/teams/Team";
import OperationsHub from "components/operations/OperationsHub";
import { WorkflowOrganizer } from "components/workflows/WorkflowOrganizer";
import { FormOrganizer } from "components/forms/FormOrganizer";

type ViewMode = "hub" | "operations" | "team" | "blueprints" | "workflows" | "forms";

const TeamsPage: React.FC = () => {
    const { artistId, teamId } = useParams<{ artistId: string; teamId: string }>();
    const [viewMode, setViewMode] = useState<ViewMode>("hub");
    const [team, setTeam] = useState<Team | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchTeam = async () => {
            if (!teamId) {
                setError("Team not found");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                const resp = await ApiService.getTeamAsync(teamId, artistId ?? "");
                if (!resp?.data?.teamId) {
                    setTeam(null);
                    setError("No team found for this teamId");
                    return;
                }
                setTeam(resp.data);
            } catch (e) {
                console.error("Failed to fetch team", e);
                setTeam(null);
                setError("Failed to fetch team");
            } finally {
                setLoading(false);
            }
        };

        void fetchTeam();
    }, [artistId, teamId]);

    if (loading) {
        return <h2 className="text-center mt-8">Loading...</h2>;
    }

    if (error || !team || !artistId || !teamId) {
        return <h2 className="text-center mt-8">{error || "Not found!"}</h2>;
    }

    const backToHub = () => setViewMode("hub");
    const backToBlueprints = () => setViewMode("blueprints");

    if (viewMode === "operations") {
        return (
            <div className="min-h-screen bg-gray-50">
                <button
                    type="button"
                    onClick={backToHub}
                    className="m-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                >
                    ← Back
                </button>
                <OperationsHub team={team} artistId={artistId} />
            </div>
        );
    }

    if (viewMode === "team") {
        return (
            <div className="min-h-screen bg-gray-50">
                <button
                    type="button"
                    onClick={backToHub}
                    className="m-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                >
                    ← Back
                </button>
                <TeamsOrganizer teamId={teamId} />
            </div>
        );
    }

    if (viewMode === "workflows") {
        return (
            <div className="min-h-screen bg-gray-50">
                <button
                    type="button"
                    onClick={backToBlueprints}
                    className="m-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                >
                    ← Back
                </button>
                <WorkflowOrganizer ownerId={teamId} />
            </div>
        );
    }

    if (viewMode === "forms") {
        return (
            <div className="min-h-screen bg-gray-50">
                <button
                    type="button"
                    onClick={backToBlueprints}
                    className="m-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                >
                    ← Back
                </button>
                <FormOrganizer ownerId={teamId} />
            </div>
        );
    }

    if (viewMode === "blueprints") {
        return (
            <div className="min-h-screen bg-gray-50 p-8">
                <button
                    type="button"
                    onClick={backToHub}
                    className="mb-6 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                >
                    ← Back
                </button>

                <h1 className="text-3xl font-bold text-center mb-8 text-gray-800">Blue Prints</h1>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                    <div
                        onClick={() => setViewMode("workflows")}
                        className="bg-white border-2 border-gray-200 rounded-lg shadow-lg p-8 cursor-pointer hover:border-blue-500 hover:shadow-xl transition-all"
                    >
                        <div className="text-center">
                            <div className="text-5xl mb-4">🔄</div>
                            <h2 className="text-2xl font-bold text-gray-800 mb-2">Workflows</h2>
                            <p className="text-gray-600">Create and manage workflows</p>
                        </div>
                    </div>

                    <div
                        onClick={() => setViewMode("forms")}
                        className="bg-white border-2 border-gray-200 rounded-lg shadow-lg p-8 cursor-pointer hover:border-blue-500 hover:shadow-xl transition-all"
                    >
                        <div className="text-center">
                            <div className="text-5xl mb-4">📝</div>
                            <h2 className="text-2xl font-bold text-gray-800 mb-2">Forms</h2>
                            <p className="text-gray-600">Design and manage forms</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // hub
    return (
        <div className="min-h-screen bg-gray-50 p-8">
            <h1 className="text-3xl font-bold text-center mb-2 text-gray-800">Team Dashboard</h1>
            <p className="text-center text-gray-600 mb-8">{team.teamName}</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
                <div
                    onClick={() => setViewMode("operations")}
                    className="bg-white border-2 border-gray-200 rounded-lg shadow-lg p-8 cursor-pointer hover:border-blue-500 hover:shadow-xl transition-all"
                >
                    <div className="text-center">
                        <div className="text-5xl mb-4">⚙️</div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Operations</h2>
                        <p className="text-gray-600">Manage projects and submissions</p>
                    </div>
                </div>

                <div
                    onClick={() => setViewMode("team")}
                    className="bg-white border-2 border-gray-200 rounded-lg shadow-lg p-8 cursor-pointer hover:border-blue-500 hover:shadow-xl transition-all"
                >
                    <div className="text-center">
                        <div className="text-5xl mb-4">👥</div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Team</h2>
                        <p className="text-gray-600">Manage members and settings</p>
                    </div>
                </div>

                <div
                    onClick={() => setViewMode("blueprints")}
                    className="bg-white border-2 border-gray-200 rounded-lg shadow-lg p-8 cursor-pointer hover:border-blue-500 hover:shadow-xl transition-all"
                >
                    <div className="text-center">
                        <div className="text-5xl mb-4">📋</div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Blue Prints</h2>
                        <p className="text-gray-600">Workflows and forms</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TeamsPage;